import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React examples
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import PieChart from "examples/Charts/PieChart";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";

export default function ReportesLogistica() {
  const [tipoReporte, setTipoReporte] = useState("general");
  const [periodo, setPeriodo] = useState("mes");
  const [zona, setZona] = useState("todas");

  // Estados para datos de reportes
  const [entregasPorZona, setEntregasPorZona] = useState({
    labels: [],
    datasets: { label: "Entregas", backgroundColors: [], data: [] },
  });

  const [costosPorTipo, setCostosPorTipo] = useState({
    labels: [],
    datasets: { label: "Costos ($)", backgroundColors: [], data: [] },
  });

  const [eficienciaVehiculos, setEficienciaVehiculos] = useState({
    labels: [],
    datasets: [],
  });

  const [tendenciaEntregas, setTendenciaEntregas] = useState({
    labels: [],
    datasets: [],
  });

  const [tiempoEntrega, setTiempoEntrega] = useState({
    labels: [],
    datasets: [],
  });

  const [satisfaccionClientes, setSatisfaccionClientes] = useState({
    labels: [],
    datasets: [],
  });

  useEffect(() => {
    cargarReportes();
  }, [tipoReporte, periodo, zona]);

  const cargarReportes = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.LOGISTICA.REPORTES, {
        params: { tipoReporte, periodo, zona },
      });

      const data = response.data;

      if (data.entregasPorZona) setEntregasPorZona(data.entregasPorZona);
      if (data.costosPorTipo) setCostosPorTipo(data.costosPorTipo);
      if (data.eficienciaVehiculos) setEficienciaVehiculos(data.eficienciaVehiculos);
      if (data.tendenciaEntregas) setTendenciaEntregas(data.tendenciaEntregas);
      if (data.tiempoEntrega) setTiempoEntrega(data.tiempoEntrega);
      if (data.satisfaccionClientes) setSatisfaccionClientes(data.satisfaccionClientes);
    } catch (error) {
      console.error("Error al cargar reportes:", error);
    }
  };

  const handleExportarExcel = () => {
    console.log("Exportando a Excel...");
  };

  const handleExportarPDF = () => {
    console.log("Exportando a PDF...");
  };

  const handleImprimir = () => {
    window.print();
  };

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDTypography variant="h5">Reportes de Logística</MDTypography>
        <MDBox display="flex" gap={1}>
          <MDButton
            variant="outlined"
            color="success"
            size="small"
            startIcon={<Icon>description</Icon>}
            onClick={handleExportarExcel}
          >
            Excel
          </MDButton>
          <MDButton
            variant="outlined"
            color="error"
            size="small"
            startIcon={<Icon>picture_as_pdf</Icon>}
            onClick={handleExportarPDF}
          >
            PDF
          </MDButton>
          <MDButton
            variant="outlined"
            color="dark"
            size="small"
            startIcon={<Icon>print</Icon>}
            onClick={handleImprimir}
          >
            Imprimir
          </MDButton>
        </MDBox>
      </MDBox>

      {/* Filtros */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Tipo de Reporte"
              value={tipoReporte}
              onChange={(e) => setTipoReporte(e.target.value)}
            >
              <MenuItem value="general">Reporte General</MenuItem>
              <MenuItem value="entregas">Entregas</MenuItem>
              <MenuItem value="costos">Análisis de Costos</MenuItem>
              <MenuItem value="vehiculos">Rendimiento de Vehículos</MenuItem>
              <MenuItem value="conductores">Desempeño de Conductores</MenuItem>
              <MenuItem value="satisfaccion">Satisfacción del Cliente</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Período"
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
            >
              <MenuItem value="hoy">Hoy</MenuItem>
              <MenuItem value="semana">Esta Semana</MenuItem>
              <MenuItem value="mes">Este Mes</MenuItem>
              <MenuItem value="trimestre">Trimestre</MenuItem>
              <MenuItem value="año">Este Año</MenuItem>
              <MenuItem value="personalizado">Personalizado</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Zona"
              value={zona}
              onChange={(e) => setZona(e.target.value)}
            >
              <MenuItem value="todas">Todas las Zonas</MenuItem>
              <MenuItem value="norte">Zona Norte</MenuItem>
              <MenuItem value="sur">Zona Sur</MenuItem>
              <MenuItem value="este">Zona Este</MenuItem>
              <MenuItem value="oeste">Zona Oeste</MenuItem>
              <MenuItem value="centro">Zona Centro</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Card>

      {/* KPIs Resumen */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDBox>
                  <MDTypography variant="caption" color="text">
                    Total Entregas
                  </MDTypography>
                  <MDTypography variant="h4" fontWeight="bold">
                    1,416
                  </MDTypography>
                </MDBox>
                <Icon fontSize="large" color="info">
                  local_shipping
                </Icon>
              </MDBox>
              <MDTypography variant="button" color="success" fontWeight="medium">
                +4.4% <span style={{ color: "#7b809a" }}>vs mes anterior</span>
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDBox>
                  <MDTypography variant="caption" color="text">
                    Tasa de Éxito
                  </MDTypography>
                  <MDTypography variant="h4" fontWeight="bold">
                    98.6%
                  </MDTypography>
                </MDBox>
                <Icon fontSize="large" color="success">
                  check_circle
                </Icon>
              </MDBox>
              <MDTypography variant="button" color="success" fontWeight="medium">
                +1.2% <span style={{ color: "#7b809a" }}>vs mes anterior</span>
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDBox>
                  <MDTypography variant="caption" color="text">
                    Tiempo Promedio
                  </MDTypography>
                  <MDTypography variant="h4" fontWeight="bold">
                    2.3 hrs
                  </MDTypography>
                </MDBox>
                <Icon fontSize="large" color="warning">
                  schedule
                </Icon>
              </MDBox>
              <MDTypography variant="button" color="success" fontWeight="medium">
                -8% <span style={{ color: "#7b809a" }}>vs mes anterior</span>
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDBox>
                  <MDTypography variant="caption" color="text">
                    Costo Total
                  </MDTypography>
                  <MDTypography variant="h4" fontWeight="bold">
                    $149.4K
                  </MDTypography>
                </MDBox>
                <Icon fontSize="large" color="error">
                  attach_money
                </Icon>
              </MDBox>
              <MDTypography variant="button" color="error" fontWeight="medium">
                +2.8% <span style={{ color: "#7b809a" }}>vs mes anterior</span>
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      {/* Gráficas de Reportes */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={3}>
                Entregas por Zona
              </MDTypography>
              <MDBox height="300px">
                <PieChart chart={entregasPorZona} height="300px" />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={3}>
                Distribución de Costos
              </MDTypography>
              <MDBox height="300px">
                <PieChart chart={costosPorTipo} height="300px" />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={3}>
                Tendencia de Entregas
              </MDTypography>
              <MDBox height="300px">
                <DefaultLineChart chart={tendenciaEntregas} height="300px" />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={3}>
                Eficiencia de Vehículos
              </MDTypography>
              <MDBox height="300px">
                <HorizontalBarChart chart={eficienciaVehiculos} height="300px" />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={3}>
                Distribución de Tiempos de Entrega
              </MDTypography>
              <MDBox height="300px">
                <VerticalBarChart chart={tiempoEntrega} height="300px" />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={3}>
                Satisfacción del Cliente
              </MDTypography>
              <MDBox height="300px">
                <HorizontalBarChart chart={satisfaccionClientes} height="300px" />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
